import type { RevenueColumn, RevenueDraft } from './revenueApi';

export const DATA_TYPES = ['INT', 'VARCHAR', 'DATE', 'FLOAT'];
export const GROUPS = ['Касса', 'Банк', 'Статистика'];
export const COLUMNS_PER_PAGE = 50;

const COLUMN_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const MAX_DISPLAY_NAME = 128;
const MAX_FORMULA = 999;

export interface DraftErrors {
  cName?: string;
  displayName?: string;
  formula?: string;
}

export function hasErrors(errors: DraftErrors): boolean {
  return !!(errors.cName || errors.displayName || errors.formula);
}

/** Пустая форма новой колонки */
export function emptyDraft(): RevenueDraft {
  return {
    cName: '',
    cType: 'INT',
    displayName: '',
    sortByGroup: GROUPS[0],
    InputBlock: 0,
    formula: '',
    important: 0,
    statisticsHide: 0,
    hideInStatTable: 0
  };
}

export function draftOf(column: RevenueColumn): RevenueDraft {
  return {
    cName: column.cName.trim(),
    cType: column.cType.trim().toUpperCase(),
    displayName: column.displayName.trim(),
    sortByGroup: column.sortByGroup,
    InputBlock: column.InputBlock,
    formula: (column.formula || '').trim(),
    important: column.important,
    statisticsHide: column.statisticsHide,
    hideInStatTable: column.hideInStatTable
  };
}

/** Формулы в фигурных скобках ({serverDate}, {pastValueEndDay}) задает система: вручную их не меняют */
export function isSystemFormula(formula: string): boolean {
  return formula.trim().charAt(0) === '{';
}

/** Форма не отличается от сохраненной колонки: отправлять на сервер нечего */
export function sameDraft(a: RevenueDraft, b: RevenueDraft): boolean {
  return (
    a.cName === b.cName &&
    a.cType === b.cType &&
    a.displayName === b.displayName &&
    a.sortByGroup === b.sortByGroup &&
    a.InputBlock === b.InputBlock &&
    a.formula === b.formula &&
    a.important === b.important &&
    a.statisticsHide === b.statisticsHide &&
    a.hideInStatTable === b.hideInStatTable
  );
}

/** Проверка формы до отправки; сервер повторяет ее и дополнительно проверяет формулу целиком */
export function validateDraft(draft: RevenueDraft, isEditing: boolean, columns: RevenueColumn[]): DraftErrors {
  const errors: DraftErrors = {};

  if (!isEditing) {
    const name = draft.cName.trim();
    if (!name) {
      errors.cName = 'Укажите имя колонки в БД';
    } else if (!COLUMN_PATTERN.test(name)) {
      errors.cName = 'Латинские буквы, цифры и «_», начинается с буквы, не длиннее 64 символов';
    } else if (columns.some((item) => item.cName.trim().toLowerCase() === name.toLowerCase())) {
      errors.cName = 'Колонка с таким именем уже есть';
    }
  }

  const display = draft.displayName.trim();
  if (!display) errors.displayName = 'Укажите отображаемое имя';
  else if (display.length > MAX_DISPLAY_NAME) errors.displayName = `Не длиннее ${MAX_DISPLAY_NAME} символов`;

  if (draft.formula.trim().length > MAX_FORMULA) errors.formula = `Формула не длиннее ${MAX_FORMULA} символов`;
  return errors;
}

/** Поиск по имени в БД, отображаемому имени и группе */
export function filterColumns(columns: RevenueColumn[], query: string): RevenueColumn[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return columns;
  return columns.filter(
    (item) =>
      item.cName.toLowerCase().indexOf(needle) >= 0 ||
      item.displayName.toLowerCase().indexOf(needle) >= 0 ||
      (item.sortByGroup || '').toLowerCase().indexOf(needle) >= 0
  );
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / COLUMNS_PER_PAGE));
}

export function pageSlice(columns: RevenueColumn[], page: number): RevenueColumn[] {
  return columns.slice((page - 1) * COLUMNS_PER_PAGE, page * COLUMNS_PER_PAGE);
}

export type Coverage = 'full' | 'partial' | 'unknown';

/** Есть ли колонка во всех таблицах аптек: если нет, отчеты аптек без нее не откроются */
export function coverageOf(column: RevenueColumn, pharmacyTables: number | null): Coverage {
  if (column.inTables === null || pharmacyTables === null) return 'unknown';
  return column.inTables >= pharmacyTables ? 'full' : 'partial';
}
